import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { isArray, isNull, isObject, isString } from 'util';
import { RouterReducerState } from '@ngrx/router-store';
import { OAuthService } from 'angular-oauth2-oidc';
import { sprintf } from 'sprintf-js';

import * as AppStateActions from '../shared/state-management/actions/app-state';
import * as selectors from '../shared/state-management/reducers';
import * as RouterActions from '../shared/state-management/actions/router';

import { getNeoConfig, getRouterState, getSelectedAgency, isBosys, openNEOInNewTab, showNeoFrame, State } from '../shared/state-management/reducers';
import { WebserviceService } from '../xena/core/shared/services/webservice/webservice.service';
import { WebstartService } from '../shared/webstart/webstart.service';
import { RouterEvent } from '../shared/models/neo-frame/router-event.model';
import { TranslateService } from '../shared/i18n/services/translate.service';
import { NeoFrameService } from './neo-frame.service';
import { WindowRef } from '../shared/services/window-ref';
import { ConfigService } from '../shared/services/config/config.service';


const buyXenaFormUrl: string = 'https://www.go-suite.com/xena-bestellen-bosys/?PLZ=%s&Reisebroname=%s&Kontaktperson=%s&StraeHausnummer=%s&Ort=%s&E-Mail=%s&Telefon=%s';


@Component({
	selector: 'neo-frame',
	templateUrl: './neo-frame.component.html',
	styleUrls: ['./neo-frame.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NeoFrameComponent implements OnInit {
	public neoUrl$: Observable<SafeResourceUrl>;
	public showNeoFrame$: Observable<boolean> = this._store.select(showNeoFrame);
	public hideXenaNeoSwitch$: Observable<boolean> = this._store.select(selectors.hideXenaNeoSwitch);

	public showTopBar$: Observable<boolean> = Observable.combineLatest(
		this._store.select(selectors.getNEODisabledState).map(disabled => !disabled),
		this._store.select(selectors.getXenaConfig).map(config => isNull(config) || isObject(config) && isObject(config.crs) && config.crs.NEO),
		this._store.select(getNeoConfig),
		(isNeoEnabled, isNeoViaConfigApiEnabled, neoConfig) => {
			return isNeoEnabled && isNeoViaConfigApiEnabled && isObject(neoConfig) && isObject(neoConfig.layout) && isString(neoConfig.layout.menuLayout) && neoConfig.layout.menuLayout !== 'new';
		}
	);

	public isXenaEnabled$ = Observable.combineLatest(
		this._store.select(selectors.getXENADisabledState).map(disabled => !disabled),
		this._webstart.getCachedWebstartByIdentifier('xena').map(webstarts => (webstarts || []).length >= 1),
		(hasBtn, hasWebstart) => hasWebstart && hasBtn,
	);

	public routeFrom: string = 'XENA';
	public routeTo: string = 'NEO';

	private _neoDisabled: boolean = false;
	private _previousUrl: string = '';

	@ViewChild('neoFrame') iframe: ElementRef;

	constructor(
		private _webservice: WebserviceService,
		private _store: Store<State>,
		private _sanitizer: DomSanitizer,
		private _webstart: WebstartService,
		private _router: Router,
		private _translateService: TranslateService,
		private _neoFrameService: NeoFrameService,
		private _windowRef: WindowRef,
		private _configService: ConfigService,
		private _oAuthService: OAuthService
	) {
		this._router.events.filter(event => event instanceof NavigationEnd).subscribe((routerEvent: RouterEvent) => {
			this._previousUrl = routerEvent.url;
		});
	}

	public ngOnInit(): void {
		const initialNeoConfigRetrival = this._store.select(getNeoConfig)
			.filter(conf => isNull(conf))
			.switchMap(() => this._store.select(getSelectedAgency))
			.filter(a => !isNull(a))
			.switchMap(() => this._configService.resolve(null, null))
			.subscribe(() => initialNeoConfigRetrival.unsubscribe());

		if (this._windowRef && this._windowRef.nativeWindow) {
			this._windowRef.nativeWindow.addEventListener('message', event => {
				if (!event.data || !isString(event.data)) {
					return;
				}

				const message = event.data;

				if (message === 'close gosuite-plattform neo-frame') {
					this._handleCloseNeoFrameMessage();
				}
			}, false);
		}

		this.neoUrl$ = this._store.select(openNEOInNewTab)
			.filter(newTabSetting => newTabSetting !== true)
			.switchMap(() => this._webstart.getCachedWebstartByIdentifier('neo'))
			.map(webstarts => {
				let lang: string = 'de_DE';
				let selectedNEOAgency: number|null = null;

				this._store.select(NeoFrameService.getSelectedNEOLanguage).take(1).subscribe(l => lang = l);
				this._store.select(selectors.getSSOSelectedAgencyId).take(1).subscribe(a => selectedNEOAgency = a);

				return isArray(webstarts) && isObject(webstarts[0]) && isString(webstarts[0].url) && webstarts[0].url.length
					? this._sanitizer.bypassSecurityTrustResourceUrl(`${webstarts[0].url}?lang=${lang}&agent=${selectedNEOAgency || ''}`)
					: null;
			});

		this._neoFrameService.reloadNeoFrame$.subscribe(reload => this._reloadNeoFrame(reload));

		this._store.select(selectors.getNEODisabledState).subscribe(neoDisabled => this._neoDisabled = neoDisabled);

		this.showNeoFrame$.subscribe(show => {
			if (show && !this._neoDisabled && this.iframe && this.iframe.nativeElement && this.iframe.nativeElement.contentWindow) {
				this._store.select(getRouterState).take(1).subscribe(router => {
					this.iframe.nativeElement.contentWindow.postMessage(JSON.stringify({function: 'gosuite-plattform-source-page', data: this._getTravelType(router)}), '*');
				});
			}
		});

		this._webservice.neoXml$.subscribe(xml => {
			if (!this._neoDisabled) {
				this._store.select(openNEOInNewTab).take(1).subscribe(openTab => {
					if (openTab) {
						let externalWindowIsActive = false;
						this._neoFrameService.externalWindowActive$.subscribe(active => externalWindowIsActive = active).unsubscribe();

						if (externalWindowIsActive) {
							this._neoFrameService.windowRef.postMessage(JSON.stringify({'function': 'importXML', data: xml, notify: false}), '*');
							this._neoFrameService.windowRef.focus();
						} else {
							this._store.dispatch(new RouterActions.OpenNEO());

							const neoWindowSub = this._neoFrameService.externalWindowActive$.subscribe(active => {
								if (active) {
									this._neoFrameService.windowRef.postMessage(JSON.stringify({'function': 'importXML', data: xml, notify: false}), '*');
									this._neoFrameService.windowRef.focus();
								}

								if (neoWindowSub) {
									neoWindowSub.unsubscribe();
								}
							});
						}
					} else {
						this._store.select(getRouterState).take(1).subscribe(router => {
							this.iframe.nativeElement.contentWindow.postMessage(JSON.stringify({'function': 'importXML', data: xml, notify: false}), '*');
							this._store.dispatch(new AppStateActions.OpenNeoFrame(this._getTravelType(router)));
						});

					}
				}).unsubscribe();
			}
		});

		this._store.select(selectors.getAppState).subscribe(appState => {
			if (appState) {
				this.routeFrom = appState.travelType;
			}
		});
	}

	public closeNeoFrame(open: boolean) {
		if (!open) {
			this._previousUrl !== '/neo' ? this._store.dispatch(new AppStateActions.CloseNeoFrame()) : this._store.dispatch(new RouterActions.Go({path: ['/xena']}));
		}
	}

	private _reloadNeoFrame(reload: boolean): void {
		if (reload && this.iframe && this.iframe.nativeElement && this.iframe.nativeElement.src) {
			const src: string = this.iframe.nativeElement.src;

			this.iframe.nativeElement.src = '';

			setTimeout(() => {
				this.iframe.nativeElement.src = src;
			}, 100);
		}
	}

	private _getTravelType(router: RouterReducerState): string {
		if (isObject(router) && isObject(router.state) && isString(router.state.url)) {
			switch (router.state.url) {
				case '/beds-and-more':
					return 'BEDS & MORE';

				case '/rental-car':
					return this._translateService.translate('Mietwagen');

				default:
					return 'XENA';
			}
		}

		return 'XENA';
	}

	private _handleCloseNeoFrameMessage(): void {
		Observable.combineLatest(
			this.isXenaEnabled$,
			this._store.select(isBosys),
			(isXenaEnabled, isBosysCustomer) => !isXenaEnabled && isBosysCustomer
		).take(1).subscribe(openBuyXenaAsBosysCustomerForm => {
			if (openBuyXenaAsBosysCustomerForm) {
				this._openBuyXenaAsBosysCustomer();
			}
		});

		this.closeNeoFrame(false);
	}

	private _openBuyXenaAsBosysCustomer(): void {
		this._store.select(getSelectedAgency).take(1).subscribe(agency => {
			const claims = this._oAuthService.getIdentityClaims();
			const customerName: string = claims['user'].first_name + ' ' + claims['user'].last_name;
			const url: string = sprintf(buyXenaFormUrl, agency.zip, agency.agentName, customerName, agency.street, agency.city, claims['user'].email, agency.phone);

			window.open(url, '_blank');
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/neo-frame/neo-frame.component.ts